import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import {
  LEDGER_TRANSACTION_KINDS,
  formatPence,
  type LedgerAccountView,
  type LedgerEntryView,
  type LedgerTransactionKind,
  type MoneyActionReply,
  type PlayerMoneyReply,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { DataTable } from '@london-life/ui/admin/data-table';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../../lib/api';
import { useAdmin } from '../../lib/admin-context';
import { when } from '../../lib/format';
import { newIdempotencyKey, parsePounds } from '../../lib/money';
import { useRequest } from '../../lib/use-request';

const kindLabel = (kind: string) => LEDGER_TRANSACTION_KINDS[kind as LedgerTransactionKind] ?? kind;
const statusBadge = (account: LedgerAccountView) =>
  account.status === 'active' ? (
    <Badge variant="secondary">Active</Badge>
  ) : (
    <Badge>{account.status === 'frozen' ? 'Frozen' : 'Closed'}</Badge>
  );
const done = (reply: MoneyActionReply) =>
  reply.status === 'done' ? 'Done.' : 'Sent for approval: a second admin must approve it in Approvals.';

function ReasonField({ id, value, onChange }: { id: string; value: string; onChange: (next: string) => void }) {
  return (
    <div className="admin-field">
      <Label htmlFor={id}>Reason</Label>
      <Textarea id={id} value={value} onChange={(event) => onChange(event.target.value)} required minLength={5} />
      <p className="admin-muted">Recorded in the ledger and the audit log, and shown to the second admin.</p>
    </div>
  );
}

// one transaction, every side of it, and the reversal button
function TransactionDialog({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: (message: string) => void;
}) {
  const { can } = useAdmin();
  const detail = useRequest(useCallback(() => api.admin.economy.transaction(id), [id]));
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState('');
  const [key] = useState(newIdempotencyKey);
  const transaction = detail.data?.transaction;
  return (
    <FormDialog
      open
      title={transaction ? kindLabel(transaction.kind) : 'Transaction'}
      description={transaction ? `${when(transaction.createdAt)} · by ${transaction.actorType}` : undefined}
      submitLabel={reversing ? 'Reverse transaction' : 'Close'}
      canSubmit={!reversing || reason.trim().length >= 5}
      onClose={onClose}
      onSubmit={async () => {
        if (!reversing) return;
        onChanged(done(await api.admin.economy.reverse(id, reason, key)));
      }}
    >
      {detail.error && <ErrorNotice error={detail.error} />}
      {transaction && (
        <>
          <DataTable
            rows={transaction.entries}
            rowKey={(entry) => entry.account.id}
            columns={[
              { key: 'account', header: 'Account', render: (entry) => entry.account.label },
              { key: 'amount', header: 'Amount', render: (entry) => formatPence(entry.amountPence) },
              { key: 'after', header: 'Balance after', render: (entry) => formatPence(entry.balanceAfterPence) },
            ]}
          />
          {transaction.reason && <p className="admin-muted">Reason: {transaction.reason}</p>}
          {transaction.reversedById && <Badge variant="outline">Reversed</Badge>}
          {can('economy.write') && !transaction.reversedById && transaction.kind !== 'reversal' && !reversing && (
            <Button type="button" variant="outline" onClick={() => setReversing(true)}>
              Reverse this transaction…
            </Button>
          )}
          {reversing && <ReasonField id="reverse-reason" value={reason} onChange={setReason} />}
        </>
      )}
    </FormDialog>
  );
}

// find a player's money by @username or email, read every movement, and act on it (audited)
export function PlayerMoneyPage() {
  const { can } = useAdmin();
  const [params, setParams] = useSearchParams();
  const accountId = params.get('account');
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<PlayerMoneyReply | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [entries, setEntries] = useState<LedgerEntryView[] | null>(null);
  const [account, setAccount] = useState<LedgerAccountView | null>(null);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [action, setAction] = useState<'freeze' | 'unfreeze' | 'adjust' | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [key, setKey] = useState(newIdempotencyKey);

  const load = useCallback(async (id: string, before?: number) => {
    setFailure(null);
    try {
      const page = await api.admin.economy.entries(id, before);
      setAccount(page.account);
      setEntries((current) => (before && current ? [...current, ...page.entries] : page.entries));
      setNextBefore(page.nextBefore);
    } catch (problem) {
      setFailure(describeError(problem));
    }
  }, []);
  useEffect(() => {
    if (accountId) void load(accountId);
  }, [accountId, load]);

  const search = async (event: FormEvent) => {
    event.preventDefault();
    setFailure(null);
    setNotice('');
    try {
      const result = await api.admin.economy.findPlayer(query);
      setFound(result);
      if (result.accounts[0]) setParams({ account: result.accounts[0].id });
      else setEntries(null);
    } catch (problem) {
      setFound(null);
      setFailure(describeError(problem));
    }
  };
  const startAction = (next: typeof action) => {
    setReason('');
    setAmount('');
    setDirection('credit');
    setKey(newIdempotencyKey());
    setAction(next);
  };
  const refresh = async (message: string) => {
    setNotice(message);
    if (accountId) await load(accountId);
  };
  const pence = parsePounds(amount);
  const writable = can('economy.write') && account?.ownerType === 'life';

  return (
    <section>
      <PageHeader
        title="Player money"
        description="Look up a player by @username or email to see every movement of their money."
      />
      <form className="admin-panel admin-page-actions" onSubmit={(event) => void search(event)}>
        <Input
          aria-label="Player @username or email"
          placeholder="@username or email"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button type="submit" disabled={query.trim().length < 3}>
          Find player
        </Button>
      </form>
      {failure && <ErrorNotice error={failure} />}
      {notice && (
        <p role="status" className="admin-panel">
          {notice}
        </p>
      )}
      {found && (
        <div className="admin-panel">
          <h2>{found.player.username ? `@${found.player.username}` : found.player.email}</h2>
          <p className="admin-muted">{found.player.email}</p>
          {found.accounts.length === 0 && <p className="admin-muted">This player has no money accounts yet.</p>}
          <div className="admin-page-actions">
            {found.accounts.map((item) => (
              <Button
                key={item.id}
                variant={item.id === accountId ? 'default' : 'outline'}
                onClick={() => setParams({ account: item.id })}
              >
                {item.label} · {formatPence(item.balancePence)}
              </Button>
            ))}
          </div>
        </div>
      )}
      {account && (
        <div className="admin-panel">
          <h2>
            {account.label}: {formatPence(account.balancePence)} {statusBadge(account)}
          </h2>
          {writable && account.status !== 'closed' && (
            <div className="admin-page-actions">
              <Button variant="outline" onClick={() => startAction('adjust')}>
                Correct balance…
              </Button>
              {account.status === 'active' ? (
                <Button variant="outline" onClick={() => startAction('freeze')}>
                  Freeze…
                </Button>
              ) : (
                <Button variant="outline" onClick={() => startAction('unfreeze')}>
                  Unfreeze…
                </Button>
              )}
            </div>
          )}
          <DataTable
            rows={entries}
            rowKey={(entry) => String(entry.entryId)}
            empty="No movements yet."
            onRowClick={(entry) => setOpened(entry.transactionId)}
            columns={[
              { key: 'when', header: 'When', render: (entry) => when(entry.createdAt) },
              { key: 'what', header: 'What', render: (entry) => kindLabel(entry.kind) },
              { key: 'amount', header: 'Amount', render: (entry) => formatPence(entry.amountPence) },
              { key: 'after', header: 'Balance after', render: (entry) => formatPence(entry.balanceAfterPence) },
              { key: 'by', header: 'By', render: (entry) => entry.actorType },
              {
                key: 'reason',
                header: 'Reason',
                render: (entry) => <span className="admin-muted">{entry.reason ?? ''}</span>,
              },
            ]}
          />
          {nextBefore && accountId && (
            <Button variant="ghost" onClick={() => void load(accountId, nextBefore)}>
              Load older movements
            </Button>
          )}
        </div>
      )}

      {opened && (
        <TransactionDialog id={opened} onClose={() => setOpened(null)} onChanged={(message) => void refresh(message)} />
      )}
      <FormDialog
        open={action === 'freeze' || action === 'unfreeze'}
        title={action === 'freeze' ? 'Freeze this account' : 'Unfreeze this account'}
        description={
          action === 'freeze'
            ? 'No money can move in or out, except by an admin, until it is unfrozen.'
            : 'The player can use this money again.'
        }
        submitLabel={action === 'freeze' ? 'Freeze' : 'Unfreeze'}
        canSubmit={reason.trim().length >= 5}
        onClose={() => setAction(null)}
        onSubmit={async () => {
          if (!account) return;
          if (action === 'freeze') await api.admin.economy.freeze(account.id, reason);
          else await api.admin.economy.unfreeze(account.id, reason);
          await refresh(action === 'freeze' ? 'Account frozen.' : 'Account unfrozen.');
        }}
      >
        <ReasonField id="freeze-reason" value={reason} onChange={setReason} />
      </FormDialog>
      <FormDialog
        open={action === 'adjust'}
        title="Correct the balance"
        description="Adds or removes money through the Corrections account. Large corrections wait for a second admin."
        submitLabel="Make correction"
        canSubmit={Boolean(pence) && reason.trim().length >= 5}
        onClose={() => setAction(null)}
        onSubmit={async () => {
          if (!account || !pence) return;
          const reply = await api.admin.economy.adjust(
            { accountId: account.id, direction, amountPence: pence, reason },
            key,
          );
          await refresh(done(reply));
        }}
      >
        <div className="admin-page-actions">
          <Button
            type="button"
            variant={direction === 'credit' ? 'default' : 'outline'}
            onClick={() => setDirection('credit')}
          >
            Add money
          </Button>
          <Button
            type="button"
            variant={direction === 'debit' ? 'default' : 'outline'}
            onClick={() => setDirection('debit')}
          >
            Remove money
          </Button>
        </div>
        <div className="admin-field">
          <Label htmlFor="adjust-amount">Amount (£)</Label>
          <Input
            id="adjust-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          {amount && !pence && <p className="admin-muted">Enter pounds, like 25 or 12.50.</p>}
        </div>
        <ReasonField id="adjust-reason" value={reason} onChange={setReason} />
      </FormDialog>
    </section>
  );
}
